"use client"

import { motion } from "framer-motion"
import Link from "next/link"
import { useEffect, useState } from "react"
import { FiArrowUpRight } from "react-icons/fi"
import AnimatedTextCycle from "@/components/ui/animated-text-cycle"
import { CambodiaTime } from "@/components/ui/cambodia-time"
import { ContactButton } from "@/components/ui/contact-button"
import { SayHelloAnnotation } from "@/components/ui/say-hello-annotation"
import { ANIMATION } from "@/lib/constants"

export default function HeroSectionMinimal() {
    const [yearsOfExperience, setYearsOfExperience] = useState(5)
    const [isContactActive, setIsContactActive] = useState(false)

    // Calculate years of experience dynamically on the client
    useEffect(() => {
        const startDate = new Date("2019-01-01") // Adjust this date to when you started web development
        const currentDate = new Date()
        const years = currentDate.getFullYear() - startDate.getFullYear()
        const months = currentDate.getMonth() - startDate.getMonth()

        // If we haven't reached the anniversary month yet, subtract 1 year
        const actualYears = months < 0 ? years - 1 : years
        setYearsOfExperience(Math.max(0, actualYears))
    }, [])

    const words = [
        "feel right to use",
        "load fast",
        "stay simple",
        "scale cleanly",
        "actually ship",
    ]

    return (
        <section className="container mx-auto flex min-h-[90vh] flex-col justify-center px-4 py-20 sm:py-0">
            {/* Top Meta Bar */}
            <div className="mb-12 flex flex-col gap-3 border-border/70 border-b pb-6 sm:flex-row sm:items-center sm:justify-between dark:border-border/40">
                <Link
                    href="/resources"
                    className="group flex items-center justify-center gap-2 font-mono text-muted-foreground text-xs uppercase tracking-widest transition-colors hover:text-foreground sm:justify-start"
                >
                    <span className="text-emerald-700 dark:text-emerald-400">
                        [NEW]
                    </span>
                    <span>Curated Resources</span>
                    <FiArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <span className="hidden font-mono text-muted-foreground text-xs uppercase tracking-widest sm:block">
                    Based in Cambodia [<CambodiaTime />]
                </span>
            </div>

            <div className="flex flex-col items-center justify-center text-center">
                {/* Text Content */}
                <div className="flex max-w-4xl flex-col items-center gap-8">
                    <motion.h1
                        initial={false}
                        animate={{ opacity: 1, y: 0 }}
                        className="font-medium text-6xl leading-[0.9] tracking-tighter sm:text-8xl md:text-9xl"
                    >
                        HELLO, I&apos;M <br />
                        <span className="text-muted-foreground transition-colors duration-300 hover:text-foreground">
                            MANTHA
                        </span>
                    </motion.h1>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="max-w-2xl font-light text-muted-foreground text-xl leading-relaxed md:text-3xl"
                    >
                        <span className="font-normal text-foreground">
                            Frontend design engineer.
                        </span>{" "}
                        Building digital experiences that{" "}
                        <AnimatedTextCycle
                            words={words}
                            interval={3500}
                            className="font-normal text-foreground"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        className="relative flex flex-col items-center gap-6 pt-4 sm:flex-row"
                    >
                        <motion.div
                            aria-hidden="true"
                            animate={
                                isContactActive
                                    ? { opacity: 0, y: 4, scale: 0.98 }
                                    : { opacity: 1, y: 0, scale: 1 }
                            }
                            transition={{
                                duration: ANIMATION.duration.slow,
                                ease: ANIMATION.ease,
                            }}
                            className="pointer-events-none absolute top-1/2 right-full mt-3 mr-4 hidden lg:block"
                        >
                            <SayHelloAnnotation className="w-[130px] text-foreground/70" />
                        </motion.div>
                        {/*<ContactButtonMinimal contactText="Let's collaborate" />*/}
                        <ContactButton
                            contactText="Let's collaborate"
                            onActiveChange={setIsContactActive}
                        />
                        <span className="font-mono text-muted-foreground text-xs uppercase tracking-widest">
                            {yearsOfExperience}+ Years of Experience
                        </span>
                    </motion.div>
                </div>
            </div>
        </section>
    )
}
